import React, { useMemo, useState } from 'react';

export type ResidencyHistoryItem = {
  id: string;
  roomNumber?: string;
  status?: 'completed' | 'terminated' | 'contracting' | 'active' | string;
  occupancyPeriod?: {
    startDate?: any;
    endDate?: any;
  };
  contractSignedAt?: any;
  completedAt?: any;
  updatedAt?: any;

  signatureName?: string;

  landlordSignedContractUrl?: string;
  landlordSignedContractUploadedAt?: any;

  contractContent?: any;

  hasExtended?: boolean;
  extensionSignatureName?: string;
  extensionContractSignedAt?: any;
  extensionLandlordSignedContractUrl?: string;
  extensionLandlordSignedContractUploadedAt?: any;
  extensionContractContent?: any;

  hasSecondExtended?: boolean;
  secondExtensionSignatureName?: string;
  secondExtensionContractSignedAt?: any;
  secondExtensionLandlordSignedContractUrl?: string;
  secondExtensionLandlordSignedContractUploadedAt?: any;
  secondExtensionContractContent?: any;
};

type ContractTab = 'original' | 'extension1' | 'extension2';

const toDate = (value: any): Date | null => {
  try {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') return value.toDate();
    if (typeof value.toMillis === 'function') return new Date(value.toMillis());
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

const formatKoreanDate = (value: any): string => {
  const date = toDate(value);
  return date ? date.toLocaleDateString('ko-KR') : '-';
};

const hasAnyContractInfo = (item: ResidencyHistoryItem, tab: ContractTab): boolean => {
  if (tab === 'original') {
    return Boolean(item.contractContent || item.landlordSignedContractUrl);
  }
  if (tab === 'extension1') {
    return Boolean(item.extensionContractContent || item.extensionLandlordSignedContractUrl);
  }
  return Boolean(item.secondExtensionContractContent || item.secondExtensionLandlordSignedContractUrl);
};

const ContractContentView: React.FC<{ content: any }> = ({ content }) => {
  const articles: Array<{ title: string; body: string }> = [
    { title: '제1조 (계약 당사자)', body: content?.article1 || '' },
    { title: '제2조 (임대차 목적물)', body: content?.article2 || '' },
    { title: '제3조 (계약 기간)', body: content?.article3 || '' },
    { title: '제4조 (임차인의 의무)', body: content?.article4 || '' },
    { title: '제5조 (계약의 효력)', body: content?.article5 || '' },
    ...(content?.article6 ? [{ title: '제6조', body: content.article6 }] : []),
    ...(content?.article7 ? [{ title: '제7조', body: content.article7 }] : []),
    ...(content?.article8 ? [{ title: '제8조', body: content.article8 }] : []),
    ...(content?.article9 ? [{ title: '제9조', body: content.article9 }] : []),
    ...(content?.article10 ? [{ title: '제10조', body: content.article10 }] : []),
    ...(content?.article11 ? [{ title: '제11조', body: content.article11 }] : [])
  ];

  return (
    <div className="space-y-4">
      {content?.header && (
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-gray-700 whitespace-pre-line">{content.header}</p>
        </div>
      )}
      {articles
        .filter((a) => a.body)
        .map((article) => (
          <div key={article.title} className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-bold text-gray-900 mb-2">{article.title}</h3>
            <p className="text-gray-700 whitespace-pre-line">{article.body}</p>
          </div>
        ))}
      {content?.footer && (
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-gray-700 whitespace-pre-line">{content.footer}</p>
        </div>
      )}
    </div>
  );
};

const ResidencyHistorySection: React.FC<{ items: ResidencyHistoryItem[] }> = ({ items }) => {
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<ResidencyHistoryItem | null>(null);
  const [tab, setTab] = useState<ContractTab>('original');

  const visibleItems = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      const aKey = toDate(a.completedAt)?.getTime() || toDate(a.updatedAt)?.getTime() || 0;
      const bKey = toDate(b.completedAt)?.getTime() || toDate(b.updatedAt)?.getTime() || 0;
      return bKey - aKey;
    });
    return expanded ? sorted : sorted.slice(0, 5);
  }, [items, expanded]);

  const openModal = (item: ResidencyHistoryItem) => {
    setSelected(item);
    setTab('original');
  };

  const closeModal = () => {
    setSelected(null);
  };

  const statusLabel = (status?: string) => {
    if (status === 'completed') return '만료(완료)';
    if (status === 'terminated') return '계약 포기(종료)';
    return status || '-';
  };

  const currentTabContent = (item: ResidencyHistoryItem, current: ContractTab) => {
    if (current === 'original') return { content: item.contractContent, url: item.landlordSignedContractUrl };
    if (current === 'extension1')
      return { content: item.extensionContractContent, url: item.extensionLandlordSignedContractUrl };
    return { content: item.secondExtensionContractContent, url: item.secondExtensionLandlordSignedContractUrl };
  };

  const tabs: Array<{ key: ContractTab; label: string }> = useMemo(() => {
    const base: Array<{ key: ContractTab; label: string }> = [{ key: 'original', label: '원 계약' }];
    if (items.some((i) => hasAnyContractInfo(i, 'extension1'))) base.push({ key: 'extension1', label: '1차 연장' });
    if (items.some((i) => hasAnyContractInfo(i, 'extension2'))) base.push({ key: 'extension2', label: '2차 연장' });
    return base;
  }, [items]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-slate-800 font-['Pretendard'] leading-8">
            과거 계약/입주 이력
          </h2>
          <p className="text-sm text-slate-600 mt-1">완료/종료된 계약을 확인하고 계약서를 열람할 수 있습니다.</p>
        </div>
        {items.length > 5 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="px-3 py-2 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors whitespace-nowrap"
          >
            {expanded ? '접기' : '더보기'}
          </button>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {visibleItems.map((item) => (
          <div
            key={item.id}
            className="border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          >
            <div className="min-w-0">
              <p className="text-base font-bold text-gray-900">
                {item.roomNumber ? `${item.roomNumber}호` : '호실 정보 없음'} · {statusLabel(item.status)}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                계약기간: {formatKoreanDate(item.occupancyPeriod?.startDate)} ~{' '}
                {formatKoreanDate(item.occupancyPeriod?.endDate)}
              </p>
              <p className="text-sm text-gray-600">
                체결일: {formatKoreanDate(item.contractSignedAt)} · 종료일: {formatKoreanDate(item.completedAt)}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => openModal(item)}
                className="px-4 py-2 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-bold"
              >
                계약서 보기
              </button>
              {item.landlordSignedContractUrl && (
                <a
                  href={item.landlordSignedContractUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-white text-blue-700 rounded-sm outline outline-1 outline-blue-200 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-bold text-center"
                >
                  PDF 열기
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-gray-900 truncate">계약서 열람</h2>
                <p className="text-sm text-gray-600 truncate">
                  {selected.roomNumber ? `${selected.roomNumber}호` : ''} · 계약기간{' '}
                  {formatKoreanDate(selected.occupancyPeriod?.startDate)} ~{' '}
                  {formatKoreanDate(selected.occupancyPeriod?.endDate)}
                </p>
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 focus:outline-none">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex flex-wrap gap-2">
                {tabs.map((t) => {
                  const disabled = !hasAnyContractInfo(selected, t.key);
                  const isActive = tab === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      disabled={disabled}
                      className={[
                        'px-3 py-2 rounded-sm text-sm font-bold transition-colors',
                        isActive ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                        disabled ? 'opacity-50 cursor-not-allowed hover:bg-gray-100' : ''
                      ].join(' ')}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {(() => {
                const { content, url } = currentTabContent(selected, tab);
                return (
                  <>
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                      <div className="text-sm text-gray-600">
                        체결일: {formatKoreanDate(selected.contractSignedAt)} · 종료일: {formatKoreanDate(selected.completedAt)}
                      </div>
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-white text-blue-700 rounded-sm outline outline-1 outline-blue-200 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-bold text-center"
                        >
                          임대인 날인본 PDF 열기/다운로드
                        </a>
                      ) : (
                        <div className="text-sm text-gray-500">임대인 날인본 PDF가 없습니다.</div>
                      )}
                    </div>

                    {content ? (
                      <ContractContentView content={content} />
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
                        저장된 계약서 내용이 없습니다. 위 PDF 버튼으로 계약서를 확인해주세요.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResidencyHistorySection;

